import axios from 'axios';
import { core as SmartyCore, usAutocompletePro } from 'smartystreets-javascript-sdk';

import { AddressDto, getGoogleMapsLoader } from '@tallo/property';

class AddressService {
  private googlePlacesClient: google.maps.places.AutocompleteService | null = null;
  private initializationPromise: Promise<void> | null = null;

  async getAddressDetails(address: string): Promise<AddressDto> {
    const response = await axios.get(`address-lookup/${address}`);

    return response.data;
  }

  async addressAutocompleteGooglePlaces(query: string): Promise<AddressDto[]> {
    await this.ensureGooglePlacesInitialized();

    return new Promise((resolve) => {
      this.googlePlacesClient!.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'us' },
        },
        (predictions, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            return resolve([]);
          }

          const addresses: AddressDto[] = predictions.map((prediction) => ({
            address: prediction.structured_formatting.main_text,
            city: prediction.structured_formatting.secondary_text,
          }));

          resolve(addresses);
        },
      );
    });
  }

  async addressAutocompleteSmartyStreets(query: string): Promise<AddressDto[]> {
    const Lookup = usAutocompletePro.Lookup;
    const credentials = new SmartyCore.SharedCredentials(process.env.SMARTY_STREETS_KEY!);
    const client = new SmartyCore.ClientBuilder(credentials)
      .withLicenses(['us-autocomplete-pro-cloud'])
      .buildUsAutocompleteProClient();

    const lookup = new Lookup(query);
    lookup.preferGeolocation = 'city';

    const { result } = await client.send(lookup);

    return result.map((suggestion): AddressDto => {
      let address = suggestion.streetLine;

      if (suggestion.secondary) {
        if (suggestion.entries > 1) {
          suggestion.secondary += ' (' + suggestion.entries + ' entries)';
        }

        address += ` ${suggestion.secondary}`;
      }

      return {
        address,
        city: suggestion.city,
        state: suggestion.state,
        zip: Number(suggestion.zipcode),
      };
    });
  }

  private async ensureGooglePlacesInitialized(): Promise<void> {
    if (this.googlePlacesClient) {
      return;
    }

    if (!this.initializationPromise) {
      this.initializationPromise = getGoogleMapsLoader()
        .importLibrary('places')
        .then(() => {
          this.googlePlacesClient = new google.maps.places.AutocompleteService();
        })
        .catch((error) => {
          console.error('Error loading Google Maps JavaScript API:', error);
          throw error;
        });
    }

    await this.initializationPromise;
  }
}

export default new AddressService();
